/**
 * File: Navbar.jsx
 * Path: /src/components/common/Navbar.jsx
 * Description: Modern header component with dropdown menu for Solutions.
 * Key Features: Clean design, responsive mobile menu, smooth transitions, and brand consistency.
 * Maintenance Notes: Modify when changing the global layout structure.
 */

import React, { useState, useEffect, Fragment } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { Menu } from '@headlessui/react';

const Navbar = ({ scrolled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Handle document body overflow when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Set mounted state to enable animations after initial render
  useEffect(() => {
    setMounted(true);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { 
      name: 'Solutions', 
      path: '/solutions',
      dropdown: true,
      items: [
        { name: 'Legal Research', path: '/solutions/legal-research', active: true },
        { name: 'Contract Analysis', path: '/solutions/contract-analysis' },
        { name: 'Legal Chronologies', path: '/solutions/legal-chronologies', active: true },
        { name: 'Judgment Prediction', path: '/solutions/judgment-prediction' },
        { name: 'OCR & Translation', path: '/solutions/ocr-translation', active: true },
        { name: 'Case Management', path: '/solutions/case-management' }
      ]
    },
    { name: 'About', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Careers', path: '/careers' },
    { name: 'Contact', path: '/contact' },
  ];
  
  // Variants for motion animations
  const variants = {
    hidden: { opacity: 0, y: -5 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
      },
    }),
  };

  return (
    <div className={`w-full transition-all duration-300 ${
      scrolled ? 'py-2' : 'py-4'
    }`}>
      <div className="container-padding mx-auto">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 z-20 relative">
            <div className={`font-display text-2xl font-bold transition-colors duration-300 ${
              scrolled ? 'text-primary-600' : 'text-dark-800'
            }`}>
              <span className="text-primary-600">Veritect</span>
              <span className="text-accent-500">.ai</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <nav className="flex space-x-6">
              {mounted && navLinks.map((link, i) => (
                <motion.div
                  key={link.name}
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={variants}
                >
                  {link.dropdown ? (
                    <Menu as="div" className="relative inline-block text-left">
                      <Menu.Button className={`font-medium transition-colors hover:text-primary-600 inline-flex items-center ${
                        scrolled ? 'text-dark-700' : 'text-dark-600'
                      }`}>
                        {link.name}
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          className="h-4 w-4 ml-1" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </Menu.Button>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                      >
                        <Menu.Items className="absolute z-10 mt-2 w-56 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none overflow-hidden">
                          <div className="py-1">
                            {link.items.map((item, index) => (
                              <Menu.Item key={index}>
                                {({ active }) => (
                                  <Link
                                    to={item.path}
                                    className={`${
                                      active ? 'bg-primary-50 text-primary-600' : 'text-dark-700'
                                    } block px-4 py-3 text-sm transition-colors duration-150 border-b border-gray-100 last:border-b-0 ${item.active ? 'text-primary-600 font-medium' : ''}`}
                                  >
                                    <div className="flex items-center">
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                      </svg>
                                      {item.name}
                                    </div>
                                  </Link>
                                )}
                              </Menu.Item>
                            ))}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </Menu>
                  ) : (
                    <NavLink
                      to={link.path}
                      className={({ isActive }) => 
                        `font-medium transition-colors hover:text-primary-600 ${
                          isActive 
                            ? 'text-primary-600' 
                            : scrolled ? 'text-dark-700' : 'text-dark-600'
                        }`
                      }
                    >
                      {link.name}
                    </NavLink>
                  )}
                </motion.div>
              ))}
            </nav>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <Link 
                to="/contact" 
                className="px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors shadow-sm hover:shadow"
              >
                Request a Demo
              </Link>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden z-20 relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              type="button"
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                scrolled || isOpen ? 'text-dark-700' : 'text-dark-600'
              } hover:text-primary-600 focus:outline-none`}
              aria-controls="mobile-menu"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {!isOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <Transition
        show={isOpen}
        enter="transition ease-out duration-200 transform"
        enterFrom="opacity-0 -translate-y-4"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150 transform"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-4"
      >
        <div className="md:hidden fixed inset-0 z-10 bg-white overflow-y-auto">
          <div className="min-h-screen flex flex-col">
            {/* Mobile menu content */}
            <div className="pt-24 pb-8 px-6 flex-grow">
              <nav className="flex flex-col space-y-1">
                {navLinks.map((link) => (
                  <div key={link.name} className="py-2 border-b border-gray-100">
                    {link.dropdown ? (
                      <div className="mb-2">
                        <div className="text-2xl font-medium text-dark-700 mb-2">{link.name}</div>
                        <div className="pl-4 space-y-2">
                          {link.items.map((item, idx) => (
                            <Link
                              key={idx}
                              to={item.path}
                              className={`flex items-center py-2 text-lg ${item.active ? 'text-primary-600' : 'text-dark-600'} hover:text-primary-600 transition-colors`}
                              onClick={() => setIsOpen(false)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-2 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <NavLink
                        to={link.path}
                        className={({ isActive }) => 
                          `text-2xl font-medium py-2 ${
                            isActive ? 'text-primary-600' : 'text-dark-700'
                          }`
                        }
                        onClick={() => setIsOpen(false)}
                      >
                        {link.name}
                      </NavLink>
                    )}
                  </div>
                ))}
                <div className="pt-6">
                  <Link 
                    to="/contact" 
                    className="block w-full text-center px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    Request a Demo
                  </Link>
                </div>
              </nav>
            </div>
            
            {/* Mobile menu footer */}
            <div className="px-6 py-8 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex space-x-4">
                  <a href="https://linkedin.com" className="text-dark-400 hover:text-primary-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M0 0v24h24v-24h-24zm8 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.397-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </a>
                  <a href="https://youtube.com" className="text-dark-400 hover:text-primary-600 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                    </svg>
                  </a>
                </div>
                <div className="text-sm text-dark-500">
                  <a href="mailto:info@veritect.ai" className="hover:text-primary-600 transition-colors">
                    info@veritect.ai
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default Navbar;